import React, { useState } from 'react';
import { Stack, Text, Box, ScrollArea, Modal, TextInput, Textarea, Button, Group } from '@mantine/core';
import { Search, CheckCircle } from 'lucide-react';
import { ProjectCard } from '../components/ProjectCard';
import type { Project } from '../types';
// Mock data
const mockProjects: Project[] = [{
  id: '1',
  name: 'Azure Bay Towers',
  location: 'Dubai Marina',
  image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&auto=format&fit=crop',
  startingPrice: 1200000,
  completionDate: 'Q4 2025',
  unitsAvailable: 45,
  amenities: ['Pool', 'Gym', 'Concierge', 'Parking', 'Kids Play Area']
}, {
  id: '2',
  name: 'Palm Vista Residences',
  location: 'Palm Jumeirah',
  image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&auto=format&fit=crop',
  startingPrice: 2500000,
  completionDate: 'Q2 2025',
  unitsAvailable: 18,
  amenities: ['Beach Access', 'Spa', 'Restaurant', 'Valet', 'Marina']
}, {
  id: '3',
  name: 'Creek Gardens',
  location: 'Dubai Creek Harbour',
  image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&auto=format&fit=crop',
  startingPrice: 850000,
  completionDate: 'Q1 2026',
  unitsAvailable: 120,
  amenities: ['Garden', 'Gym', 'Pool', 'BBQ Area', 'Jogging Track']
}, {
  id: '4',
  name: 'Downtown Elite',
  location: 'Downtown Dubai',
  image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&auto=format&fit=crop',
  startingPrice: 1800000,
  completionDate: 'Q3 2025',
  unitsAvailable: 32,
  amenities: ['Burj View', 'Infinity Pool', 'Smart Home', 'Concierge']
}];
export function ProjectDiscovery() {
  const [searchQuery, setSearchQuery] = useState('');
  const [interestModalOpen, setInterestModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const filteredProjects = mockProjects.filter(project => project.name.toLowerCase().includes(searchQuery.toLowerCase()) || project.location.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleInterested = (project: Project) => {
    setSelectedProject(project);
    setSubmitted(false);
    setInterestModalOpen(true);
  };
  const handleSubmitInterest = () => {
    setSubmitted(true);
    setTimeout(() => {
      setInterestModalOpen(false);
      setSubmitted(false);
    }, 2000);
  };
  return <>
      <ScrollArea h="100%" offsetScrollbars data-id="element-382">
        <Stack gap="lg" pb={100} data-id="element-383">
          {/* Header */}
          <Box data-id="element-384">
            <Text fw={700} size="xl" c="#1a2332" mb={4} data-id="element-385">
              Discover Projects
            </Text>
            <Text size="sm" c="#64748b" data-id="element-386">
              Explore new investment opportunities
            </Text>
          </Box>

          {/* Search */}
          <TextInput placeholder="Search by project or location..." leftSection={<Search size={18} color="#64748b" data-id="element-388" />} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} radius="md" size="md" styles={{
          input: {
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            '&:focus': {
              borderColor: '#10b981'
            }
          }
        }} data-id="element-387" />

          {/* Project Grid */}
          <Stack gap="md" data-id="element-389">
            {filteredProjects.length === 0 ? <Box p="xl" style={{
            backgroundColor: '#f8fafc',
            borderRadius: 12,
            textAlign: 'center'
          }} data-id="element-390">
                <Text c="#64748b" data-id="element-391">No projects found</Text>
              </Box> : filteredProjects.map(project => <ProjectCard key={project.id} project={project} onInterested={() => handleInterested(project)} data-id="element-392" />)}
          </Stack>
        </Stack>
      </ScrollArea>

      {/* Interest Modal */}
      <Modal opened={interestModalOpen} onClose={() => setInterestModalOpen(false)} title={!submitted && <Text fw={700} size="lg" c="#1a2332" data-id="element-394">
              Express Interest
            </Text>} radius="md" centered size="md" data-id="element-393">
        {submitted ? <Stack align="center" gap="md" py="xl" data-id="element-395">
            <Box p="lg" style={{
          backgroundColor: '#d1fae5',
          borderRadius: '50%'
        }} data-id="element-396">
              <CheckCircle size={48} color="#10b981" data-id="element-397" />
            </Box>
            <Text fw={700} size="lg" c="#1a2332" ta="center" data-id="element-398">
              Interest Submitted!
            </Text>
            <Text size="sm" c="#64748b" ta="center" data-id="element-399">
              Our sales team will contact you within 24 hours.
            </Text>
          </Stack> : <Stack gap="md" data-id="element-400">
            {selectedProject && <Box p="md" style={{
          backgroundColor: '#f8fafc',
          borderRadius: 12
        }} data-id="element-401">
                <Text fw={600} c="#1a2332" data-id="element-402">
                  {selectedProject.name}
                </Text>
                <Text size="sm" c="#64748b" data-id="element-403">
                  {selectedProject.location}
                </Text>
              </Box>}

            <TextInput label="Full Name" placeholder="Enter your name" radius="md" defaultValue="Ahmed Al-Rashid" data-id="element-404" />

            <TextInput label="Phone Number" placeholder="+971 50 XXX XXXX" radius="md" defaultValue="+971 50 123 4567" data-id="element-405" />

            <TextInput label="Email" placeholder="your@email.com" radius="md" defaultValue="ahmed@example.com" data-id="element-406" />

            <Textarea label="Message (Optional)" placeholder="Any specific requirements or questions?" minRows={3} radius="md" data-id="element-407" />

            <Group justify="flex-end" gap="sm" mt="md" data-id="element-408">
              <Button variant="subtle" color="gray" radius="md" onClick={() => setInterestModalOpen(false)} data-id="element-409">
                Cancel
              </Button>
              <Button color="green" radius="md" onClick={handleSubmitInterest} styles={{
            root: {
              backgroundColor: '#10b981',
              '&:hover': {
                backgroundColor: '#059669'
              }
            }
          }} data-id="element-410">
                Submit Interest
              </Button>
            </Group>
          </Stack>}
      </Modal>
    </>;
}